<template>
    <div class="main box">
        <h1>Users</h1>
        <users-tabs></users-tabs>

        <form class="invite-container" @submit.prevent="invite">
            <div class="invite-label">Invite a user : an email lets them choose their own password.</div>
            <div class="invite-row">
                <input type="text" placeholder="First name*" maxlength="50" v-model.trim="form.firstname" />
                <input type="text" placeholder="Last name*" maxlength="50" v-model.trim="form.lastname" />
                <input type="email" placeholder="Email*" maxlength="150" v-model.trim="form.email" />
                <select v-model="form.roleKey">
                    <option value="" disabled>Role*</option>
                    <option v-for="role in roles" :key="role.key" :value="role.key">{{ role.name }}</option>
                </select>
                <input type="submit" class="btn" :disabled="!canInvite || sending" :value="sending ? 'Sending...' : 'Invite'" />
            </div>
            <div class="notices errors" v-if="inviteError">{{ inviteError }}</div>
            <div class="invite-link" v-if="lastLink">
                Invitation link (valid 7 days), to copy if the email does not arrive :
                <input type="text" readonly :value="lastLink" @focus="$event.target.select()" />
            </div>
        </form>

        <div class="table-container">
            <table class="cards">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Last sign-in</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in users" :key="user._id">
                        <td data-label="Name">{{ user.firstname }} {{ user.lastname }}</td>
                        <td data-label="Email">{{ user.email }}</td>
                        <td data-label="Role">
                            <select :value="user.roleKey || ''" @change="changeRole(user, $event)">
                                <option value="" disabled>No role</option>
                                <option v-for="role in roles" :key="role.key" :value="role.key">{{ role.name }}</option>
                            </select>
                        </td>
                        <td data-label="Status"><span :class="['status-badge', { off : user.isActive === false }]">{{ user.isActive === false ? 'Disabled' : 'Active' }}</span></td>
                        <td data-label="Last sign-in">{{ formatDate(user.lastConnection) }}</td>
                        <td data-label="Actions">
                            <div class="actions-container">
                                <div class="post-btn icon-otw_icon_edit" title="Edit the name" @click="openEdit(user)"></div>
                                <div class="post-btn icon-otw_icon_email" title="Send a new invitation link (the current password keeps working)" v-if="user.isActive !== false" @click="resend(user)"></div>
                                <div class="post-btn text-btn" title="Invalidate the current password and open sessions, and send a link" v-if="user.isActive !== false && user._id != $store.state.user.id" @click="confirmReset = user">Reset password</div>
                                <div class="post-btn text-btn" @click="toggleActive(user)" v-if="user._id != $store.state.user.id">{{ user.isActive === false ? 'Enable' : 'Disable' }}</div>
                                <div class="post-btn icon-otw_icon_trash" title="Delete" v-if="user._id != $store.state.user.id && user.isDeletable !== false" @click="confirmUser = user"></div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="modal" v-if="editUser">
            <form class="modal-container" @submit.prevent="saveEdit">
                <h3>Edit user</h3>
                <p class="confirm-name">{{ editUser.email }}</p>
                <input type="text" class="edit-input" placeholder="First name*" maxlength="50" v-model.trim="editUser.firstname" />
                <input type="text" class="edit-input" placeholder="Last name*" maxlength="50" v-model.trim="editUser.lastname" />
                <div class="notices errors" v-if="editError">{{ editError }}</div>
                <div class="btns-container">
                    <div class="btn btn-cancel" @click="editUser = null">Cancel</div>
                    <input type="submit" class="btn btn-confirm" :disabled="!editUser.firstname || !editUser.lastname" value="Save" />
                </div>
            </form>
        </div>

        <div class="modal" v-if="confirmReset">
            <div class="modal-container">
                <h3>Reset the password of this user ?</h3>
                <p class="confirm-name">{{ confirmReset.firstname }} {{ confirmReset.lastname }}</p>
                <p class="confirm-help">Their current password stops working and their open sessions are closed. They receive an email with a link to choose a new password (valid 7 days).</p>
                <div class="btns-container">
                    <div class="btn btn-cancel" @click="confirmReset = null">Cancel</div>
                    <div class="btn btn-confirm" @click="forceReset">Confirm</div>
                </div>
            </div>
        </div>

        <div class="modal" v-if="confirmUser">
            <div class="modal-container">
                <h3>Please confirm deletion of this user</h3>
                <p class="confirm-name">{{ confirmUser.firstname }} {{ confirmUser.lastname }}</p>
                <div class="btns-container">
                    <div class="btn btn-cancel" @click="confirmUser = null">Cancel</div>
                    <div class="btn btn-confirm" @click="deleteUser">Confirm</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import UsersTabs from "./../components/UsersTabs.vue";

export default {
    name : "Users",
    components : { UsersTabs },
    data() {
        return {
            users : [],
            roles : [],
            form : { firstname : "", lastname : "", email : "", roleKey : "" },
            sending : false,
            inviteError : null,
            lastLink : null,
            confirmUser : null,
            confirmReset : null,
            editUser : null,
            editError : null
        }
    },
    computed : {
        canInvite() {
            const f = this.form;
            return f.firstname && f.lastname && f.email && f.roleKey;
        }
    },
    methods : {
        // request helper: calls `then` with the response, shows a notice on network error
        api(method, path, data) {
            return axios({
                method : method,
                url : `${this.$store.state.apiUrl}${path}`,
                data : data,
                validateStatus : () => true,
                headers : { 'x-access-token' : localStorage._safeToken }
            }).catch(() => ({ status : 0, data : { errors : "Unable to reach the API" } }));
        },
        errorText(res) {
            const errors = res.data && (res.data.errors || res.data.message);
            return Array.isArray(errors) ? errors.join(" ") : (typeof errors == "string" ? errors : "An error occurred.");
        },
        notify(msg, error) {
            this.$store.commit('setNotice', { msg : msg, error : error });
        },
        formatDate(date) {
            return date ? new Date(date).toLocaleDateString("EN-en", { year: 'numeric', month: '2-digit', day: '2-digit' }) : "-";
        },
        async load() {
            const [users, roles] = await Promise.all([this.api("get", "/users"), this.api("get", "/roles")]);
            if(users.status != 200 || roles.status != 200) {
                this.notify(this.errorText(users.status != 200 ? users : roles), true);
                return;
            }
            this.users = users.data.message;
            this.roles = roles.data.message;
        },
        async invite() {
            if(!this.canInvite || this.sending) return;
            this.sending = true;
            this.inviteError = null;
            this.lastLink = null;
            const res = await this.api("post", "/users", this.form);
            this.sending = false;
            if(res.status != 200) {
                this.inviteError = this.errorText(res);
                return;
            }
            this.lastLink = res.data.link;
            this.form = { firstname : "", lastname : "", email : "", roleKey : "" };
            this.notify("User created, invitation sent.", false);
            this.load();
        },
        async resend(user) {
            const res = await this.api("post", `/users/${user._id}/invite`);
            if(res.status != 200) {
                this.notify(this.errorText(res), true);
                return;
            }
            this.lastLink = res.data.link;
            this.notify("A new invitation has been sent.", false);
        },
        openEdit(user) {
            this.editError = null;
            this.editUser = { _id : user._id, email : user.email, firstname : user.firstname, lastname : user.lastname };
        },
        async saveEdit() {
            const edited = this.editUser;
            this.editError = null;
            const res = await this.api("put", `/users/${edited._id}`, { firstname : edited.firstname, lastname : edited.lastname });
            if(res.status != 200) {
                this.editError = this.errorText(res);
                return;
            }
            const user = this.users.find(u => u._id == edited._id);
            if(user) {
                user.firstname = edited.firstname;
                user.lastname = edited.lastname;
            }
            this.editUser = null;
            this.notify("User updated.", false);
        },
        async forceReset() {
            const user = this.confirmReset;
            const res = await this.api("post", `/users/${user._id}/force-reset`);
            this.confirmReset = null;
            if(res.status != 200) {
                this.notify(this.errorText(res), true);
                return;
            }
            this.lastLink = res.data.link;
            this.notify("Password reset, an email has been sent.", false);
        },
        async changeRole(user, event) {
            const roleKey = event.target.value;
            const res = await this.api("put", `/users/${user._id}`, { roleKey : roleKey });
            if(res.status != 200) {
                event.target.value = user.roleKey || "";
                this.notify(this.errorText(res), true);
                return;
            }
            user.roleKey = roleKey;
            this.notify("Role updated.", false);
        },
        async toggleActive(user) {
            const isActive = user.isActive === false;
            const res = await this.api("put", `/users/${user._id}`, { isActive : isActive });
            if(res.status != 200) {
                this.notify(this.errorText(res), true);
                return;
            }
            user.isActive = isActive;
            this.notify(isActive ? "User enabled." : "User disabled.", false);
        },
        async deleteUser() {
            const user = this.confirmUser;
            const res = await this.api("delete", `/user/${user._id}`);
            this.confirmUser = null;
            if(res.status != 200) {
                this.notify(this.errorText(res), true);
                return;
            }
            this.users = this.users.filter(u => u._id != user._id);
            this.notify("User deleted.", false);
        }
    },
    mounted() {
        this.load();
    }
}
</script>

<style lang="scss" scoped>
    h1 {
        color:#fff;
        text-align: center;
        font-size:25px;
        text-transform: uppercase;
        font-weight: 300;
        margin:40px auto 20px auto;
    }

    .invite-container {
        margin: 0 auto 40px auto;
        padding: 20px;
        border: 1px solid #333;
    }

    .invite-label {
        color:#fff;
        font-size:14px;
        margin-bottom:15px;
    }

    .invite-row {
        display: flex;
        align-items: center;
        gap:15px;
    }

    .invite-row input[type="text"], .invite-row input[type="email"], .invite-row select {
        flex:1;
        min-width:0;
    }

    .invite-row .btn {
        padding:11px 25px;
        font-size:15px;
        text-transform: uppercase;
    }

    .invite-link {
        margin-top:15px;
        color:#fff;
        font-size:13px;
    }

    .invite-link input {
        margin-top:8px;
        width:100%;
    }

    .notices.errors {
        margin-top:15px;
    }

    td select {
        min-width:130px;
    }

    .status-badge {
        padding:3px 10px;
        font-size:12px;
        text-transform: uppercase;
        border:1px solid $mainColor;
        color:$mainColor;
    }

    .status-badge.off {
        border-color:$red;
        color:$red;
    }

    .actions-container {
        display: flex;
        align-items: center;
        justify-content: center;
        gap:15px;
        flex-wrap: wrap;
    }

    .text-btn {
        width:auto;
        height:auto;
        font-size:13px;
        text-transform: uppercase;
        cursor: pointer;
    }

    .confirm-name {
        color:#fff;
        margin-top:10px;
    }

    .confirm-help {
        color:#fff;
        font-size:14px;
        font-weight:300;
        text-align:center;
        margin-top:15px;
        line-height:1.4em;
    }

    .edit-input {
        width:100%;
        margin-top:15px;
    }

    .modal {
        position:fixed;
        top:0;
        bottom:0;
        left:0;
        right:0;
        z-index:1;
        background:rgba(255,255,255, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .modal-container {
        padding:40px;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        background:#000;
        width:500px;
    }

    .modal-container h3 {
        font-size:20px;
        color:#fff;
        text-align: center;
        font-weight: 300;
    }

    .btns-container {
        display: flex;
        margin-top:20px;
    }

    .btns-container .btn {
        margin:0 20px;
    }

    .btn-cancel {
        background:#000;
        border:1px solid #fff;
    }

    .btn-cancel:hover {
        background:#fff;
        color:#000;
    }

    @media (max-width: 1024px) {
        .invite-row {
            flex-wrap: wrap;
        }

        .invite-row input[type="text"], .invite-row input[type="email"], .invite-row select {
            flex:1 1 40%;
        }
    }

    @media (max-width: 700px) {
        .invite-row {
            flex-direction: column;
            align-items: stretch;
        }

        .invite-row input[type="text"], .invite-row input[type="email"], .invite-row select {
            flex:none;
            width:100%;
            box-sizing: border-box;
        }

        .invite-container {
            padding:15px;
        }

        .text-btn {
            white-space: nowrap;
        }
    }
</style>
